/**
 * Locating the ISF JSON header inside a `.fs` file: the one definition of
 * "where the header is", shared by the web's ISF toolchain (src/isf/scan.ts
 * re-exports it) and the server (shared/isfShaderKind.js), so the two can never
 * disagree on whether a source is ISF.
 *
 * Two deliberate departures from the reference C++ implementation in VIDOS
 * (`src/graphics/ISFParser.cpp`), both of which VIDOS should adopt:
 *
 *  1. VIDOS searches for `/*{` anywhere in the file, so a licence banner that
 *     happens to contain that sequence turns a native shader into an ISF one.
 *     We require the header to be the FIRST non-whitespace token.
 *  2. VIDOS brace-counts with no string or escape awareness, so a DESCRIPTION
 *     containing `}` truncates the header and the shader silently falls back to
 *     "native". We track string state and escapes.
 *
 * Pure and dependency-free (plain JS so node can import it without a build).
 */

/**
 * Whether `c` is skippable whitespace. A UTF-8 byte-order mark counts: editors
 * on Windows prepend one, and it must not turn an ISF file into a legacy one.
 * @param {string} c
 * @returns {boolean}
 */
export function isSpace(c) {
  return c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f' || c === '\v' ||
    c === '﻿';
}

/** Index of the first character at or after `from` that is not whitespace, or -1. */
function firstNonSpace(source, from) {
  for (let i = from; i < source.length; i++) {
    if (!isSpace(source[i])) return i;
  }
  return -1;
}

/**
 * Index one past the closing quote of the JSON string literal whose opening
 * quote is at `open`, honouring backslash escapes; -1 if it never closes.
 * @param {string} source
 * @param {number} open
 * @returns {number}
 */
export function skipString(source, open) {
  for (let i = open + 1; i < source.length; i++) {
    const c = source[i];
    if (c === '\\') i++;
    else if (c === '"') return i + 1;
  }
  return -1;
}

/**
 * Find the matching `}` for the `{` at `open`, respecting JSON string literals
 * and backslash escapes. Returns the index one past the closing brace, or -1 if
 * the dict is never closed.
 */
function matchBrace(source, open) {
  let depth = 0;

  for (let i = open; i < source.length; i++) {
    const c = source[i];

    if (c === '"') {
      const close = skipString(source, i);
      if (close < 0) return -1;
      i = close - 1;
    } else if (c === '{') {
      depth++;
    } else if (c === '}') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }

  return -1;
}

/**
 * Locate the ISF header comment, or return null when the source does not open
 * with one. Does not validate the JSON (src/isf/parse.ts's job).
 * @param {string} source
 * @returns {{commentStart: number, jsonStart: number, jsonEnd: number, commentEnd: number} | null}
 */
export function findHeaderSpan(source) {
  const commentStart = firstNonSpace(source, 0);
  if (commentStart < 0) return null;
  if (source[commentStart] !== '/' || source[commentStart + 1] !== '*') return null;

  const jsonStart = firstNonSpace(source, commentStart + 2);
  if (jsonStart < 0 || source[jsonStart] !== '{') return null;

  const jsonEnd = matchBrace(source, jsonStart);
  if (jsonEnd < 0) return null;

  const closer = source.indexOf('*/', jsonEnd);
  const commentEnd = closer < 0 ? source.length : closer + 2;

  return { commentStart, jsonStart, jsonEnd, commentEnd };
}
